import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_QUINT } from './ease';

/**
 * Living Structure のシグネチャー画像コンポーネント。
 *
 * mode="reveal": ベースはモノクロ。カーソル周辺だけフルカラーが灯る
 *                （radial-gradientマスクをlerp追従）。タッチ環境では
 *                ビューポート中央に来たとき全体がカラーに変わる。
 * mode="lit":    ベースはフルカラー。カーソル周辺の明度が上がる（ヒーロー用）。
 *
 * curtain: 初回表示時にペーパー色のカーテンが持ち上がり、内部画像が
 *          scale 1.15→1 で落ち着く導入リビール。
 */
export default function LivingImage({
  src,
  alt = '',
  mode = 'reveal',
  radius = 240,
  className = '',
  imgClassName = '',
  curtain = true,
  sweep = true,
  eager = false,
  revealDelay = 0,
  children,
}) {
  const frameRef = useRef(null);
  const topRef = useRef(null);
  const state = useRef({ x: 0, y: 0, tx: 0, ty: 0, raf: null });
  const [hovered, setHovered] = useState(false);
  const [fine, setFine] = useState(true);
  const reduce = useReducedMotion();

  useEffect(() => {
    setFine(window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    const s = state.current;
    return () => {
      if (s.raf != null) cancelAnimationFrame(s.raf);
    };
  }, []);

  const paint = useCallback(() => {
    const s = state.current;
    s.x += (s.tx - s.x) * 0.16;
    s.y += (s.ty - s.y) * 0.16;
    if (topRef.current) {
      const m = `radial-gradient(circle ${radius}px at ${s.x}px ${s.y}px, black 40%, transparent 100%)`;
      topRef.current.style.webkitMaskImage = m;
      topRef.current.style.maskImage = m;
    }
    if (Math.abs(s.tx - s.x) + Math.abs(s.ty - s.y) > 0.3) {
      s.raf = requestAnimationFrame(paint);
    } else {
      s.raf = null;
    }
  }, [radius]);

  const toLocal = (e) => {
    const r = frameRef.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const onEnter = (e) => {
    if (mode === 'plain') {
      if (e.pointerType === 'mouse') setHovered(true);
      return;
    }
    if (e.pointerType !== 'mouse' || !frameRef.current) return;
    const [lx, ly] = toLocal(e);
    const s = state.current;
    s.x = s.tx = lx;
    s.y = s.ty = ly;
    paint();
    setHovered(true);
  };
  const onMove = (e) => {
    if (mode === 'plain' || e.pointerType !== 'mouse' || !frameRef.current) return;
    const [lx, ly] = toLocal(e);
    const s = state.current;
    s.tx = lx;
    s.ty = ly;
    if (s.raf == null) s.raf = requestAnimationFrame(paint);
  };
  const onLeave = (e) => {
    if (e.pointerType !== 'mouse') return;
    setHovered(false);
  };

  // reveal: 静止時はミュートカラー（アクセントがほのかに見える）→
  //         ホバーで全体が鮮やかに戻り、カーソル周辺はさらに明るく「灯る」
  const baseFilter =
    mode === 'reveal'
      ? hovered
        ? 'grayscale(0) saturate(1.22) brightness(1.04)'
        : 'grayscale(0.72) brightness(0.99)'
      : 'none';
  const topFilter =
    mode === 'lit'
      ? 'brightness(1.2) saturate(1.18)'
      : fine
        ? 'brightness(1.14) saturate(1.32)'
        : 'saturate(1.15)';

  // タッチ環境: reveal はスクロールで自動点灯、lit は無効
  const coarseReveal = !fine && mode === 'reveal';

  return (
    <div
      ref={frameRef}
      className={`relative overflow-hidden ${className}`}
      onPointerEnter={onEnter}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
    >
      <motion.img
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        className={`h-full w-full object-cover will-change-transform ${imgClassName}`}
        style={{ filter: baseFilter, transition: 'filter 0.9s ease' }}
        initial={curtain && !reduce ? { scale: 1.15 } : false}
        whileInView={curtain && !reduce ? { scale: 1 } : undefined}
        viewport={{ once: true, margin: '-8%' }}
        transition={{ duration: 1.5, ease: EASE_QUINT, delay: revealDelay }}
      />

      {mode !== 'plain' && !(mode === 'lit' && !fine) && (
        <motion.img
          ref={topRef}
          src={src}
          alt=""
          aria-hidden="true"
          loading={eager ? 'eager' : 'lazy'}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{ filter: topFilter }}
          initial={{ opacity: 0 }}
          animate={fine ? { opacity: hovered ? 1 : 0 } : undefined}
          whileInView={coarseReveal ? { opacity: 1 } : undefined}
          viewport={coarseReveal ? { once: false, amount: 0.55 } : undefined}
          transition={{ duration: fine ? 0.45 : 1.1, ease: 'easeOut' }}
        />
      )}

      {sweep && !reduce && fine && (
        <motion.span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-[-12%] left-0 w-1/4 rotate-[6deg] bg-gradient-to-r from-transparent via-white/25 to-transparent"
          initial={false}
          animate={
            hovered
              ? { x: ['-180%', '540%'], opacity: [0, 1, 0] }
              : { x: '-180%', opacity: 0, transition: { duration: 0 } }
          }
          transition={{ duration: 1.15, ease: 'easeInOut' }}
        />
      )}

      {curtain && !reduce && (
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 bg-paper"
          initial={{ y: '0%' }}
          whileInView={{ y: '-101%' }}
          viewport={{ once: true, margin: '-8%' }}
          transition={{ duration: 1.05, ease: EASE_QUINT, delay: revealDelay }}
        />
      )}

      {children}
    </div>
  );
}
